import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Wallet, TrendingUp, Receipt, CheckSquare, Users, Sparkles, Loader2, ArrowUpRight, CircleDot, Handshake, ArrowRight, CalendarClock, FileText, ChevronDown, RefreshCw, Plane, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import QuickAdd from "@/components/QuickAdd";
import { AuroraSwirl } from "@/components/AuroraSwirl";

const PIE_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

const REMINDER_META = {
  event: { icon: CalendarClock, text: "text-accent", bg: "bg-accent/10" },
  task: { icon: CheckSquare, text: "text-blue-500", bg: "bg-blue-500/10" },
  invoice: { icon: FileText, text: "text-amber-500", bg: "bg-amber-500/10" },
  recurring_invoice: { icon: RefreshCw, text: "text-accent", bg: "bg-accent/10" },
  deal: { icon: Handshake, text: "text-violet-500", bg: "bg-violet-500/10" },
};

const REMINDER_ROUTE = {
  event: "/calendar",
  task: "/tasks",
  invoice: "/finance?tab=invoices",
  recurring_invoice: "/finance?tab=invoices",
  deal: "/clients?tab=opportunities",
};

function CollapsibleCard({ title, count, testId, className = "", children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 sm:p-6 ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          <span className="font-display text-base font-semibold">{title}</span>
          {count > 0 && (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">{count}</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-4 animate-fade-up">{children}</div>}
    </div>
  );
}


function ChartCard({ title, children, className = "", testId }) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 sm:p-6 ${className}`} data-testid={testId}>
      <h3 className="font-display text-base font-semibold">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "12px", fontSize: "12px" },
  labelStyle: { color: "hsl(var(--foreground))" },
  itemStyle: { color: "hsl(var(--foreground))" },
};

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [insights, setInsights] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  const getInsights = async () => {
    setInsightLoading(true);
    setInsights("");
    try {
      const res = await api.post("/ai/insights");
      setInsights(res.data.insights);
      refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInsightLoading(false);
    }
  };

  if (isLoading) return <PageLoader />;
  const m = data.metrics;

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[460px] overflow-hidden opacity-60" aria-hidden="true">
        <AuroraSwirl />
      </div>
      <div className="relative">
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0]}`}
        subtitle={`Here's how ${user?.business?.name || "your business"} is doing today.`}
        testId="dashboard-header"
        actions={
          <div className="flex items-center gap-2">
            <QuickAdd />
            <Button onClick={getInsights} disabled={insightLoading} data-testid="ai-insights-button">
              {insightLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              AI Insights
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Revenue" value={money(m.revenue)} sub="Paid invoices" icon={TrendingUp} accent="accent" testId="stat-revenue" delay={0} />
        <StatCard label="Outstanding" value={money(m.outstanding)} sub="Awaiting payment" icon={Wallet} accent="amber" testId="stat-outstanding" delay={60} />
        <StatCard label="Expenses" value={money(m.expenses)} sub="Logged total" icon={Receipt} accent="rose" testId="stat-expenses" delay={120} />
        <StatCard label="Net position" value={money(m.net)} sub="Revenue − expenses" icon={ArrowUpRight} accent="primary" testId="stat-net" delay={180} />
      </div>

      {insights && (
        <div className="mt-6 rounded-2xl border border-accent/30 bg-accent/5 p-5 animate-fade-up" data-testid="ai-insights-panel">
          <div className="mb-2 flex items-center gap-2 text-accent"><Sparkles className="h-4 w-4" /><span className="text-sm font-semibold">AI Insights</span></div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{insights}</div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {data.reminders && data.reminders.length > 0 && (
          <CollapsibleCard title="Reminders — next 7 days" count={data.reminders.length} testId="dashboard-reminders">
            <div className="grid gap-2 sm:grid-cols-2">
              {data.reminders.map((r, i) => {
                const meta = REMINDER_META[r.type] || REMINDER_META.event;
                const Icon = meta.icon;
                return (
                  <button type="button" key={`${r.type}-${i}`} onClick={() => navigate(REMINDER_ROUTE[r.type] || "/calendar")} className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-secondary/60" data-testid={`reminder-${r.type}-${i}`}>
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.bg} ${meta.text}`}><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.title}</p>
                      {r.subtitle && <p className="truncate text-xs text-muted-foreground">{r.subtitle}</p>}
                    </div>
                    {r.overdue ? (
                      <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">Overdue</span>
                    ) : (
                      <span className="shrink-0 text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <button onClick={() => navigate("/calendar")} className="mt-3 flex items-center gap-1 text-sm font-medium text-accent hover:underline" data-testid="reminders-view-calendar">
              View calendar <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </CollapsibleCard>
        )}

        <CollapsibleCard title="Upcoming tasks" count={data.upcoming_tasks.length} testId="dashboard-tasks">
          <div className="space-y-2.5">
            {data.upcoming_tasks.length ? data.upcoming_tasks.map((t) => (
              <button key={t.id} onClick={() => navigate("/tasks")} className="flex w-full items-start gap-2.5 rounded-lg border border-border p-2.5 text-left transition-colors hover:bg-secondary/60" data-testid={`dashboard-task-${t.id}`}>
                <CircleDot className={`mt-0.5 h-4 w-4 shrink-0 ${t.priority === "high" ? "text-rose-500" : t.priority === "medium" ? "text-amber-500" : "text-muted-foreground"}`} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t.title}</p>
                  <p className="text-xs text-muted-foreground">Due {t.due_date}</p>
                </div>
              </button>
            )) : <p className="py-6 text-center text-sm text-muted-foreground">All caught up 🎉</p>}
          </div>
        </CollapsibleCard>

        {data.upcoming_trips && data.upcoming_trips.length > 0 && (
          <CollapsibleCard title="Upcoming trips" count={data.upcoming_trips.length} testId="dashboard-trips">
            <div className="space-y-2.5">
              {data.upcoming_trips.map((t) => (
                <button key={t.id} onClick={() => navigate(`/travel?trip=${t.id}`)} className="flex w-full items-start gap-2.5 rounded-lg border border-border p-2.5 text-left transition-colors hover:bg-secondary/60" data-testid={`dashboard-trip-${t.id}`}>
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{t.destination}</p>
                    <p className="text-xs text-muted-foreground">{t.start_date ? `${t.start_date}${t.end_date ? ` → ${t.end_date}` : ""}` : "Dates TBD"}{t.status === "active" ? " · Active" : ""}</p>
                  </div>
                  <Plane className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CollapsibleCard>
        )}

        <CollapsibleCard title="Recent activity" count={data.activities.length} testId="dashboard-activity">
          <div className="space-y-3">
            {data.activities.length ? data.activities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-sm" data-testid={`activity-${a.id}`}>
                <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />
                <span className="flex-1 text-foreground/90">
                  {a.actor_name && a.actor_name !== "System" && (
                    <span className="font-medium text-foreground">{a.actor_name} · </span>
                  )}
                  {a.description}
                </span>
                <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            )) : <p className="py-6 text-center text-sm text-muted-foreground">No activity yet.</p>}
          </div>
        </CollapsibleCard>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <ChartCard title="Revenue vs Expenses (6 mo)" className="lg:col-span-2" testId="chart-revenue">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.revenue_series} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip {...tooltipStyle} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expenses" fill="hsl(var(--chart-4))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Expense breakdown" testId="chart-expenses">
          {data.expense_breakdown.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.expense_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.expense_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="py-16 text-center text-sm text-muted-foreground">No expenses yet.</p>}
        </ChartCard>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <ChartCard title="Cash flow forecast (3 mo)" className="lg:col-span-2" testId="chart-forecast">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.forecast}>
              <defs>
                <linearGradient id="fc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-3))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip {...tooltipStyle} formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="projected" stroke="hsl(var(--chart-3))" strokeWidth={2.5} fill="url(#fc)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
          <StatCard label="Open tasks" value={m.open_tasks} icon={CheckSquare} accent="blue" testId="stat-tasks" />
          <StatCard label="Contacts" value={m.contacts} sub={`${m.leads} prospects`} icon={Users} accent="accent" testId="stat-contacts" />
        </div>
      </div>

      </div>
    </div>
  );
}
